import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { SubscriptionList } from '../../shared/models/asp.types';
import * as util from '../../shared/services/util.service';
import { ICustomer } from '@signal/asp-data-commons';
import { confirmConfigChecklist } from '../../shared/services/util.service';
import { SettingsState } from '../../store/settings/settings.reducer';
import * as fromSettingsActions from '../../store/settings/settings.actions';
import { Departments } from '../../shared/enums/departments.enum';
import { AspBannerService } from '../../shared/services/asp-banner.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { IFooterSummary } from '../../store/sales-appointment/sales-appointment.reducer';
import { VehicleCustomerDetailsComponent } from './vehicle-customer-details/vehicle-customer-details.component';
import { DeliveryChecklistState } from '../../store/delivery-checklist/delivery-checklist.reducer';
import { descriptionOfProductsOrServices, selectDigiChecklistFooterSummaries, selectEditDetails } from '../../store/delivery-checklist/delivery-checklist.selectors';
import { DigiChecklistComponent } from './digi-checklist/digi-checklist.component';
import { CreateDigiChekReq, EditDigiCheckReq, GetEditData, ResetDigiCheckState, UpdateDescription } from '../../store/delivery-checklist/delivery-checklist.actions';
import { SignaturesComponent } from './signatures/signatures.component';

@Component({
  selector: 'app-create-delivery-checklist',
  templateUrl: './create-delivery-checklist.component.html',
  styleUrls: ['./create-delivery-checklist.component.scss']
})
export class CreateDeliveryChecklistComponent implements OnInit, OnDestroy {
  isSubmitting = false;
  isSaveForLaterSubmitting = false;
  selectedIndex = 0;
  continueBtnTrans = 'testDrive.continue';
  continueBtnText: string[] = [this.continueBtnTrans, 'testDrive.submit', 'testDrive.submit', 'testDrive.submitting'];
  digiChecklistFooterSummaries$: Observable<IFooterSummary[]>;
  toShowFooter = true;
  department: string = Departments.SALES;
  breadcrumbSteps: object[] = [
    { name: 'testDrive.vehicle', id: 0 },
    { name: 'delivery.checkList', id: 1 }
  ];
  @ViewChild(VehicleCustomerDetailsComponent, { static: false }) vehicleCustomerDetailsComponent: VehicleCustomerDetailsComponent;
  @ViewChild(DigiChecklistComponent, { static: false }) digiChecklistComponent: DigiChecklistComponent;
  @ViewChild(SignaturesComponent, { static: false }) signaturesComponent: SignaturesComponent;

  private readonly subs: SubscriptionList = {};
  languageCd = 'en';
  formChanged = true;
  customerDetails$: Observable<ICustomer>;
  isUpdate = false;
  digiChekId: string;
  vin: string;
  isSubmitAllowed = false;
  productsOrServicesDescription: string = '';
  doVinsMatch=false;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly deliveryChecklistState: Store<DeliveryChecklistState>,
    private readonly settingsState: Store<SettingsState>,
    private readonly bannerService: AspBannerService,
    private readonly translate: TranslateService,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    if (this.translate.currentLang?.includes('es')) {
      this.languageCd = 'es';
    }

    this.subs.query = this.activatedRoute.queryParams.subscribe(params => {
      this.digiChekId = params.id;
      this.vin = params.vin;
      if (this.digiChekId) {
        this.deliveryChecklistState.dispatch(new GetEditData
          ({ digiChekId: this.digiChekId }));
        this.isUpdate = true;
        this.deliveryChecklistState.select(descriptionOfProductsOrServices).subscribe(value => {
          this.productsOrServicesDescription = value;
        });
      }
    });

    this.settingsState.dispatch(new fromSettingsActions.LoadSettings({ department: this.department }));
  }

  canExit(): Observable<boolean> {
    const result = new Subject<boolean>();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, confirmConfigChecklist);
    this.subs.dialogRef = dialogRef.afterClosed().subscribe(event => {
      if (event.action === 'close') {
        result.next(true);
        result.complete();
      }
    });
    return result.asObservable();
  };

  onDescriptionChange(event) {
    this.deliveryChecklistState.dispatch(new UpdateDescription({description:this.productsOrServicesDescription}));
  }

  backToDashboard() {
    this.router.navigate(['asp/deliveryChecklist/summary/pending']);
  }

  validateRowLimit(event: Event) {
    if(this.productsOrServicesDescription?.split("\n").length > 3) {
      event.preventDefault();
    }
  }

  formsNotEdited() {
    return this.formChanged;
  }

  valuesChanges(event) {
    this.formChanged = event;
  }

  ngOnInit(): void {
    this.setupStore();
  }

  ngAfterViewInit() {
    this.bannerService.show();
  }

  switchTab(tabIndex?) {
    if (this.toShowFooter) {
      if (tabIndex !== undefined && tabIndex >= 0) {
          this.setSelectedIndex(tabIndex);
      } else if (this.isValidForm()) {
          this.setSelectedIndex(this.selectedIndex + 1);
          if (this.selectedIndex === 2 && this.isValidSignatureForm()) {
            this.submitTheAppointment();
          }
      }
    }
  }

  setSelectedIndex(index: number) {
    this.selectedIndex = index;

    if (index === 3) {
      this.bannerService.hideCarImage();
    } else if (index === 2) {
      this.bannerService.showCarImage();
    } else {
      this.bannerService.showCarImage();
    }
    const container = document.querySelector('.delivery');
    container.scrollTo(0, 0);
  }

  gotoBack() {
      this.setSelectedIndex(this.selectedIndex - 1);
  }

  isValidDigiCheckForm() {
    return this.digiChecklistComponent?.isValidForm();
  }

  isValidSignatureForm() {
    return this.signaturesComponent.customerSignatureObject.id && this.signaturesComponent.technicianSignatureObject.id;
  }

  isValidForm() {
    if (this.selectedIndex === 0) {
      return this.vehicleCustomerDetailsComponent?.isValidForm() && this.isSubmitAllowed;
    }
    else if (this.selectedIndex === 1) {
      return this.digiChecklistComponent?.isValidForm() && this.isValidSignatureForm();
    }
    return false;
  }

  isValidSaveForLaterForm() {
    return this.vehicleCustomerDetailsComponent?.isValidSaveForLaterForm() && (this.isSubmitAllowed ? 
      !(this.signaturesComponent?.technicianSignatureObject?.data && this.signaturesComponent?.customerSignatureObject?.data) : true);
  }

  checkNav(tabNo: number) {
    switch (tabNo) {
      case 0:
        return true;
      case 1:
        return this.areStepsValid(0);
      default:
        return false;
    }
  }

  areStepsValid(...steps: number[]) {
    for (const step of steps) {
      if (!this.isStepValid(step)) {
        return false;
      }
    }
    return true;
  }

  isStepValid(step: number) {
    switch (step) {
      case 0:
        return this.vehicleCustomerDetailsComponent?.isValidForm();
      case 1:
        return this.digiChecklistComponent?.isValidForm() && this.isValidSignatureForm();
      default:
        return false;
    }
  }

  ngAfterContentChecked(): void {
    if (!this.changeDetector['destroyed']) {
      this.changeDetector.detectChanges();
    }
  }

  ngOnDestroy(): void {
    util.unsubscribeSubscriptions(this.subs);
    this.bannerService.update('');
    this.deliveryChecklistState.dispatch(new ResetDigiCheckState());
    this.changeDetector.detach();
  }

  navigateToPending(timer){
    setTimeout(() => {
      this.isSaveForLaterSubmitting = false;
          this.router.navigate(['asp/deliveryChecklist/summary/pending']);
        }, timer);
  }

  onSubmittingSaveForLater(data) {
    if (data) {
      if (data.isSubmitted) {
        this.navigateToPending(5000);
      }else {
        this.navigateToPending(10000);
      }
    }
  }

  onSubmitSaveForLater() {
    this.isSaveForLaterSubmitting = true;
    this.onSubmit(true);
  }

  onSubmitting(data) {
    if (data) {
      if (data.isSubmitted) {
        setTimeout(() => {
      this.isSubmitting = false;
          this.router.navigate(['asp/deliveryChecklist/summary/completed']);
        }, 5000);
      }else {
        setTimeout(() => {
          this.isSubmitting = false;
              this.router.navigate(['asp/deliveryChecklist/summary/pending']);
            }, 10000);
      }
    }else {
      setTimeout(() => {
        this.isSubmitting = false;
            this.router.navigate(['asp/deliveryChecklist/summary/pending']);
          }, 10000);
    }
  }

  onSubmit(isSaveForLater: boolean) {
    this.formChanged = true;
    if (!this.isUpdate) {
      this.deliveryChecklistState.dispatch(new CreateDigiChekReq({ isSaveForLater, isVinValid: this.isSubmitAllowed }));
    }

    else if (this.isUpdate) {
      this.deliveryChecklistState.dispatch(new EditDigiCheckReq({ isSaveForLater, isVinValid: this.isSubmitAllowed }));

    }
    this.subs.editDetails = this.deliveryChecklistState.select(selectEditDetails)
      .subscribe((editDetail) => {
        this.formChanged = true;
        isSaveForLater ? this.onSubmittingSaveForLater(editDetail) : this.onSubmitting(editDetail);
      });

  }

  private submitTheAppointment() {
    this.isSubmitting = true;
    this.onSubmit(false);
  }

  private setupStore() {
    this.digiChecklistFooterSummaries$ = this.deliveryChecklistState.select(selectDigiChecklistFooterSummaries);
  }

  enableButton(event)
  {
    this.isSubmitAllowed = event;
  }

  matchVins(event)
  {
    this.doVinsMatch=event;
  }
}

